import { ISLBatchHeaderRef, ISLBatchHeader } from "./SLBatchHeader";
import { slBatchEntryFactory } from "../../services/SLBatchEntryFactory";
import { RuntimeError, InvalidEntryError, SLEntriesError } from "../../services/ErrorHelpers";
import { postAttachment } from "../../services/AttachmentClient";

import { IAttachmentEntryFormRef } from "../Attachments";
import { IAttachment } from "../../schemas/SLAttachment";
import { IValidationMessage } from "../../schemas/ISLEntryResponse";
import { AxiosResponse } from "axios";
import { saveSLBatch } from "./SLBatchClient";
import { IUserProviderState } from "../../contexts/UserContext";
import { ISLBatchResponse } from "./SLBatchSchemas";

function createSlBatchEntry(batchObject: ISLBatchHeader, secondaryAttachments: IAttachment[]) {
  return {
    batchName: batchObject.name,
    batchDescription: batchObject.description,
    purpose: batchObject.purpose,
    attachmentsList: secondaryAttachments
  };
}

export async function assembleSLEntry(
  getTokenFn: IUserProviderState["accessToken"],
  headerRef: React.RefObject<ISLBatchHeaderRef>,
  attachmentsRef: React.RefObject<IAttachmentEntryFormRef>
): Promise<{
  batchFile: IAttachment | null;
  entry: ReturnType<typeof createSlBatchEntry>;
}> {
  if (headerRef.current && attachmentsRef.current) {
    const header = headerRef.current.getHeader();
    if (!header.result.file && !header.result.batchFileInfo) {
      throw new RuntimeError("Batch File is required.");
    }
    let attachmentBatch: IAttachment | undefined;
    if (header.result.file) {
      const attachment = await postAttachment(header.result.file, getTokenFn);
      attachmentBatch = {
        blobName: attachment.blobName,
        fileName: attachment.inputFileName,
        url: attachment.fileStorageUri,
        fileSize: attachment.fileSize,
        uploadedOn: attachment.uploadedOn
      };
    }
    const secondaryAttachments = attachmentsRef.current.getAttachments();
    return {
      batchFile: attachmentBatch ? attachmentBatch : header.result.batchFileInfo,
      entry: createSlBatchEntry(header.result, secondaryAttachments)
    };
  } else {
    // A SubComponent needs to crash to reach here
    throw new Error("Ref Mount Error");
  }
}

export function loadSLBatchEntry(
  headerRef: React.RefObject<ISLBatchHeaderRef>,
  attachmentsRef: React.RefObject<IAttachmentEntryFormRef>,
  slBatchEntry: ISLBatchResponse
): void {
  const batchEntryData = slBatchEntryFactory.mapBatchDataToStoreInterfaces(slBatchEntry);
  if (headerRef.current && attachmentsRef.current) {
    headerRef.current.setHeader(batchEntryData.entryHeaderData);
    attachmentsRef.current.setAttachments(batchEntryData.attachmentsListData);
  } else {
    // A SubComponent needs to crash to reach here
    throw new Error("Ref Mount Error");
  }
}

function formatValidationErrors(resultMessages: IValidationMessage[]): string[] {
  const messages = resultMessages.map((payload) => payload.errorMessage);
  return messages;
}

function handleErrorFromAxios(response: AxiosResponse<IValidationMessage[]>) {
  // We catch the possible API Error, SLEntries Returns an array of objects with ErrorMessages in it
  const postStatus = Math.floor(response.status / 100);
  if (postStatus === 4) {
    // 400 level
    const errorMessages = formatValidationErrors(response.data);
    return new SLEntriesError(["Invalid input.", ...errorMessages]);
  }
  return new SLEntriesError(["Internal server error. Please try again later."]);
}

export async function saveBatchEntry(
  id: number,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  slBatchEntry: any,
  batchFile: IAttachment,
  getTokenFn: IUserProviderState["accessToken"],
  refGUID: string | null,
  rowVer: ArrayBuffer | null
): Promise<{
  messages: string[];
  batchEntry: ISLBatchResponse;
}> {
  try {
    const payload = slBatchEntryFactory.getSLBatchPostBody(id, slBatchEntry, batchFile, refGUID, rowVer);
    if (!payload) {
      throw new InvalidEntryError("Invalid entry - changes not saved.");
    }

    const saveBatchResponse = await saveSLBatch(payload, getTokenFn);
    const batchEntry = saveBatchResponse.data as ISLBatchResponse;
    const batchRefGUID = saveBatchResponse.data.refGUID;

    return {
      messages: [
        `Batch has been validated and submitted for review, please see dashboard drafts for if there are any errors on that batch. Batch refGUID is ${batchRefGUID}.`
      ],
      batchEntry
    };
  } catch (possibleAPIError) {
    if (possibleAPIError.response) {
      const response = possibleAPIError.response as AxiosResponse<IValidationMessage[]>;
      // the below will handle the error and rethrow it as something more manageable
      throw handleErrorFromAxios(response);
    } else {
      // Some other non-axios error
      throw possibleAPIError;
    }
  }
}
